import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Sparkle, Copy, Eye, EyeSlash, Trash, Article } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function JournalPage() {
  const [blogTopic, setBlogTopic] = useState("");
  const [blogKeywords, setBlogKeywords] = useState("");
  const [blogTone, setBlogTone] = useState("professional, friendly");
  const [blog, setBlog] = useState(null);
  const [blogLoading, setBlogLoading] = useState(false);

  const [saved, setSaved] = useState([]);
  const loadSaved = () => api.get("/seo/blog").then((r) => setSaved(r.data)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  const runBlog = async () => {
    if (!blogTopic) return toast.error("Enter a topic");
    setBlogLoading(true);
    try {
      const r = await api.post("/seo/blog", {
        topic: blogTopic,
        keywords: blogKeywords.split(",").map((s) => s.trim()).filter(Boolean),
        tone: blogTone,
        word_count: 800,
      });
      setBlog(r.data);
      toast.success("Blog post generated");
      loadSaved();
    } catch {
      toast.error("Failed to generate blog");
    } finally { setBlogLoading(false); }
  };

  const togglePublish = async (b) => {
    await api.patch(`/seo/blog/${b.id}`, { published: !b.published });
    toast.success(b.published ? "Hidden from Journal" : "Published to Journal");
    loadSaved();
  };
  const removeBlog = async (id) => {
    if (!window.confirm("Delete this draft permanently?")) return;
    await api.delete(`/seo/blog/${id}`);
    toast.success("Deleted");
    loadSaved();
  };

  return (
    <div data-testid="journal-page">
      <PageHeader
        eyebrow="Content"
        title="Journal & Blog"
        description="Write AI-powered SEO articles and publish them to the public Journal."
      />

      <Tabs defaultValue="generate" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-sm p-1 h-auto">
          <TabsTrigger data-testid="tab-blog-generate" value="generate" className="rounded-sm px-4 py-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white">Blog Generator</TabsTrigger>
          <TabsTrigger data-testid="tab-blog-drafts" value="drafts" className="rounded-sm px-4 py-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Drafts &amp; Published{saved.length > 0 && <Badge className="ml-2 bg-navy text-white border-0 rounded-sm">{saved.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="sharp-card p-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Topic</Label>
                  <Input data-testid="blog-topic" placeholder="e.g. 5 signs you need gutter cleaning" value={blogTopic} onChange={(e) => setBlogTopic(e.target.value)} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Target keywords</Label>
                  <Input data-testid="blog-keywords" placeholder="comma-separated" value={blogKeywords} onChange={(e) => setBlogKeywords(e.target.value)} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Tone</Label>
                  <Input data-testid="blog-tone" value={blogTone} onChange={(e) => setBlogTone(e.target.value)} className="rounded-sm mt-1" />
                </div>
                <Button data-testid="btn-generate-blog" onClick={runBlog} disabled={blogLoading} className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11">
                  <Sparkle className="mr-2" size={18} />{blogLoading ? "Writing..." : "Generate blog post"}
                </Button>
              </div>
            </Card>
            <Card className="sharp-card p-6 lg:col-span-2 min-h-[400px]">
              {!blog ? (
                <div className="text-slate-400 text-sm h-full flex items-center justify-center">Your draft will appear here.</div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="eyebrow eyebrow-dark">Draft preview</div>
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(blog.content || ""); toast.success("Copied"); }}>
                      <Copy size={12} className="mr-1.5" />Copy
                    </Button>
                  </div>
                  <h2 className="font-display text-3xl text-navy tracking-wide leading-tight mb-2">{blog.topic}</h2>
                  <div className="text-xs text-slate-500 mb-4">/{blog.slug}</div>
                  <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line">{blog.content}</div>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="drafts" className="mt-6">
          <Card className="sharp-card p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="eyebrow eyebrow-dark">Drafts &amp; published</div>
                <p className="text-sm text-slate-600 mt-1">Published articles appear on the public <a href="/blog" target="_blank" rel="noreferrer" className="text-navy underline">Journal</a> page.</p>
              </div>
            </div>
            {saved.length === 0 ? (
              <div className="text-center text-slate-500 py-12">
                <Article size={32} className="mx-auto text-slate-300" />
                <p className="mt-3 text-sm">No drafts yet — head to the Blog Generator tab.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {saved.map((b) => (
                  <div key={b.id} data-testid={`draft-${b.id}`} className="py-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-bold text-navy">{b.topic || "Untitled draft"}</div>
                        {b.published
                          ? <Badge className="bg-emerald-100 text-emerald-800 border-0 rounded-sm text-[10px] uppercase tracking-wider">Published</Badge>
                          : <Badge variant="outline" className="rounded-sm border-slate-300 text-slate-600 text-[10px] uppercase tracking-wider">Draft</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">/{b.slug} · {new Date(b.created_at).toLocaleDateString()}</div>
                      <p className="text-sm text-slate-700 mt-2 line-clamp-2">{(b.content || "").slice(0, 220)}…</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button data-testid={`draft-toggle-${b.id}`} variant="outline" size="sm" className="rounded-sm" onClick={() => togglePublish(b)}>
                        {b.published ? <><EyeSlash size={12} className="mr-1"/>Unpublish</> : <><Eye size={12} className="mr-1"/>Publish</>}
                      </Button>
                      <Button data-testid={`draft-del-${b.id}`} variant="outline" size="sm" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" onClick={() => removeBlog(b.id)}>
                        <Trash size={12} className="mr-1"/>Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
